import * as React from "react"
import PropTypes from "prop-types"
import YouTubePlayer from "react-player/youtube"
import Head from "next/head"
import { toPrivacyEnhancedYouTubeUrl } from "lib/videoAutoplayPolicy"

function LiteYouTubeEmbed(props) {
  const [preconnected, setPreconnected] = React.useState(false)
  typeof props.playlistCoverId === "string"
    ? encodeURIComponent(props.playlistCoverId)
    : null
  const videoTitle = props.title
  const announceWatch = props.announce || "Watch"
  const posterUrl = props.poster + "?f=auto"
  const activatedClassImp = props.activatedClass || "lyt-activated"
  const aspectHeight = props.aspectHeight || 9
  const aspectWidth = props.aspectWidth || 16
  const playerClassImp = props.playerClass || "lty-playbtn"
  const wrapperClassImp = props.wrapperClass || "yt-lite"
  const onIframeAdded = props.onIframeAdded || function () {}
  const rel = props.rel ? "prefetch" : "preload"
  const ContainerElement = props.containerElement || "article"
  const playerUrl = toPrivacyEnhancedYouTubeUrl(props.url)

  const warmConnections = () => {
    if (preconnected) return
    setPreconnected(true)
  }

  React.useEffect(() => {
    if (props.active) {
      setPreconnected(true)
      onIframeAdded()
    }
  }, [props.active])

  return (
    <>
      {preconnected && (
        <Head>
          <link rel="preconnect" href="https://cdn.nobodysurf.com" />
          <link rel="dns-prefetch" href="https://cdn.nobodysurf.com" />
          <link rel={rel} href={posterUrl} as="image" />
        </Head>
      )}
      <ContainerElement
        onPointerOver={warmConnections}
        onClick={props.active ? undefined : props.onActivate}
        className={`${wrapperClassImp} ${
          props.active ? activatedClassImp : ""
        }`}
        data-title={videoTitle}
        style={{
          backgroundImage: `url(${posterUrl})`,
          ...{
            "--aspect-ratio": `${(aspectHeight / aspectWidth) * 100}%`,
          },
        }}>
        {!props.active && (
          <button
            type="button"
            className={playerClassImp}
            aria-label={`${announceWatch} ${videoTitle}`}
          />
        )}
        {props.active && playerUrl && (
          <YouTubePlayer
            key={props.playerKey}
            className="absolute inset-0 z-10"
            url={playerUrl}
            playsinline={true}
            muted={true}
            onReady={props.onReady}
            onStart={props.onStart}
            onPlay={props.onPlay}
            onPause={props.onPause}
            onEnded={props.onEnded}
            onDuration={props.onDuration}
            onProgress={props.onProgress}
            controls={true}
            playing={props.playing}
            width="100%"
            height="100%"
            onError={props.onError}
            config={{
              youtube: {
                embedOptions: {
                  host: "https://www.youtube-nocookie.com",
                },
              },
            }}
          />
        )}
      </ContainerElement>
    </>
  )
}

LiteYouTubeEmbed.propTypes = {
  active: PropTypes.bool.isRequired,
  activatedClass: PropTypes.string,
  announce: PropTypes.string,
  aspectHeight: PropTypes.number,
  aspectWidth: PropTypes.number,
  containerElement: PropTypes.elementType,
  onActivate: PropTypes.func.isRequired,
  onDuration: PropTypes.func.isRequired,
  onEnded: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onIframeAdded: PropTypes.func,
  onPause: PropTypes.func.isRequired,
  onPlay: PropTypes.func.isRequired,
  onProgress: PropTypes.func.isRequired,
  onReady: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired,
  playerClass: PropTypes.string,
  playerKey: PropTypes.number.isRequired,
  playing: PropTypes.bool.isRequired,
  playlistCoverId: PropTypes.string,
  poster: PropTypes.string.isRequired,
  rel: PropTypes.bool,
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  wrapperClass: PropTypes.string,
}

export default LiteYouTubeEmbed
